import axios from 'axios';

// Default and bounds for game-level pagination on /odds_screen
export const ODDS_SCREEN_PAGE_SIZE = 200;
const MAX_PAGE_SIZE = 500;
const MIN_PAGE_SIZE = 1;

function clampPageSize(limit) {
	const size = typeof limit === 'number' && !isNaN(limit) ? limit : ODDS_SCREEN_PAGE_SIZE;
	return Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE_SIZE, size));
}

/**
 * Fetch paginated game-grouped odds from the backend odds screen endpoint.
 *
 * Backend contract (see migration guide):
 *   GET /odds_screen?league&market&outcome_id_normalized_no_book&is_live&page&limit
 *
 * Returns the backend response in a normalized shape:
 *   {
 *     games: GameGroup[],
 *     pagination: {
 *       page,
 *       limit,
 *       total,
 *       totalPages,
 *       hasNext,
 *       hasPrev,
 *     }
 *   }
 */
export async function fetchOddsScreen(params) {
	const { league, market, outcomeIdNormalizedNoBook, isLive, page, limit, accessToken } = params || {};

	if (page == null || page < 1) {
		throw new Error(`fetchOddsScreen requires a positive page number (got ${page})`);
	}

	const effectiveLimit = clampPageSize(limit);

	const searchParams = new URLSearchParams();
	if (league) searchParams.set('league', league);
	if (market) searchParams.set('market', market);
	if (outcomeIdNormalizedNoBook) {
		searchParams.set('outcome_id_normalized_no_book', outcomeIdNormalizedNoBook);
	}
	if (typeof isLive === 'boolean') {
		searchParams.set('is_live', isLive ? 'true' : 'false');
	}
	searchParams.set('page', String(page));
	searchParams.set('limit', String(effectiveLimit));

	const headers = {};
	if (accessToken) {
		headers.Authorization = `Bearer ${accessToken}`;
	}

	const baseUrl = process.env.REACT_APP_BACKEND_URL || '';

	const response = await axios.get(`${baseUrl}/odds_screen?${searchParams.toString()}`, {
		headers,
	});

	const data = response?.data || {};
	const games = Array.isArray(data.data) ? data.data : [];
	const pagination = data.pagination || {
		page,
		limit: effectiveLimit,
		total: games.length,
		totalPages: 1,
		hasNext: false,
		hasPrev: page > 1,
	};

	return { games, pagination };
}

